import { ControllerRenderProps, type FieldPath, type FieldValues, type UseFormReturn, useFormContext } from "react-hook-form"
import {
  FormField as ShadcnFormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form"

interface FormFieldProps<T extends FieldValues> {
  name: FieldPath<T>
  label?: string
  description?: string
  render: (props: {
    field: ControllerRenderProps<T, FieldPath<T>>
    fieldState: { invalid: boolean; error?: { message?: string } }
  }) => React.ReactNode
  form?: UseFormReturn<T> // Make form optional
  isOptionalField?: boolean
}

export function FormField<T extends FieldValues>({ form, name, label, description, render, isOptionalField=false }: FormFieldProps<T>) {
  // Use the provided form or get it from context
  const formContext = useFormContext() as UseFormReturn<T> | null
  const formToUse = form || formContext

  if (!formToUse) {
    throw new Error("FormField must be used within a Form or be passed a form prop")
  }

  return (
    <ShadcnFormField
      control={formToUse.control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem>
          {label && <FormLabel className={`${isOptionalField ? 'required-field' : ''}`}>{label}</FormLabel>}
          <FormControl>{render({ field, fieldState })}</FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage className="text-red-500" />
        </FormItem>
      )}
    />
  )
}
